import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useSiteSeo } from "../../context/SiteSeoContext";
import { absoluteCanonicalUrl } from "../../utils/siteUrl";

/**
 * Rank-Math-style head tags: title, description, canonical, OG, Twitter, optional JSON-LD.
 */
export default function Seo({
  title,
  description,
  canonicalPath,
  /** Override full canonical URL when needed */
  canonicalUrl,
  ogImage,
  ogImageAlt,
  /** 'website' | 'article' | 'product' */
  ogType = "website",
  keywords,
  noindex = false,
  /**
   * When noindex=true, default robots is noindex,nofollow. Set true for flows like login
   * so bots may still discover links out (minimal crawl impact — mostly clarity).
   */
  noindexFollow = false,
  /** Array of serializable JSON-LD objects (each becomes one script tag) */
  jsonLd = null,
}) {
  const site = useSiteSeo();
  const { pathname } = useLocation();
  const canonical =
    canonicalUrl ||
    absoluteCanonicalUrl(
      canonicalPath != null
        ? canonicalPath.startsWith("/")
          ? canonicalPath
          : `/${canonicalPath}`
        : pathname
    );

  const defaultOg = site.default_og_image_url ? String(site.default_og_image_url).trim() : "";
  const resolvedOg =
    ogImage && String(ogImage).trim() ? String(ogImage).trim() : defaultOg ? defaultOg : "";
  const ogAlt =
    ogImageAlt && String(ogImageAlt).trim()
      ? String(ogImageAlt).trim()
      : title
        ? `${title}`
        : site.organization_name || "Gem Fashion Studios";

  const tw = site.twitter_handle ? `@${site.twitter_handle.replace(/^@+/, "")}` : "";

  const scriptBlocks = [];
  if (Array.isArray(jsonLd) && jsonLd.length) {
    jsonLd.forEach((obj, i) => {
      if (obj && typeof obj === "object") {
        scriptBlocks.push(
          <script key={`ld-${i}`} type="application/ld+json">
            {JSON.stringify(obj)}
          </script>
        );
      }
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      {description ? <meta name="description" content={description} /> : null}
      {keywords ? <meta name="keywords" content={keywords} /> : null}

      <link rel="canonical" href={canonical} />

      {noindex ? (
        <meta name="robots" content={noindexFollow ? "noindex, follow" : "noindex, nofollow"} />
      ) : (
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      )}

      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={title} />
      {description ? <meta property="og:description" content={description} /> : null}
      <meta property="og:url" content={canonical} />
      {resolvedOg ? <meta property="og:image" content={resolvedOg} /> : null}
      {resolvedOg && ogAlt ? <meta property="og:image:alt" content={ogAlt} /> : null}

      <meta name="twitter:card" content={resolvedOg ? "summary_large_image" : "summary"} />
      {tw ? <meta name="twitter:site" content={tw} /> : null}
      <meta name="twitter:title" content={title} />
      {description ? <meta name="twitter:description" content={description} /> : null}
      {resolvedOg ? <meta name="twitter:image" content={resolvedOg} /> : null}

      {scriptBlocks}
    </Helmet>
  );
}
